import { Stores } from '../../../../libraries/storage/constants';
import type { AnnotationFrameRecord, AnnotationMetadataRecord, FetchedRangesRecord } from '../../../../libraries/storage/types';
import { DIRTY_FLUSH_DEBOUNCE_MS, DIRTY_FLUSH_THRESHOLD, VIDEO_ID } from '../constants';
import type { AnnotationWritePayload, MetadataPayload } from '../types';
import type { FrameAnnotationKey, IAnnotationPersistence } from './annotation-persistence';
export class DirtySetPersistence implements IAnnotationPersistence {
  private readonly taskKey: string;
  private readonly petition: () => void;
  private readonly dirtyFrames = new Map<string, AnnotationFrameRecord | 'DELETE'>();
  private readonly dirtyMetadata = new Map<string, AnnotationMetadataRecord | 'DELETE'>();
  private pendingFetchedRanges: FetchedRangesRecord | null = null;
  private flushDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  constructor(taskKey: string, petition: () => void) {
    this.taskKey = taskKey;
    this.petition = petition;
  }
  onWrite(frameIndex: number, annotationId: string, payload: AnnotationWritePayload): void {
    this.dirtyFrames.set(this.makeFrameKey(frameIndex, annotationId), this.makeFrameRecord(frameIndex, annotationId, payload));
    this.scheduleDebouncedFlushCheck();
  }
  onDelete(frameIndex: number, annotationId: string): void {
    this.dirtyFrames.set(this.makeFrameKey(frameIndex, annotationId), 'DELETE');
    this.scheduleDebouncedFlushCheck();
  }
  onMetadataWrite(annotationId: string, metadata: MetadataPayload): void {
    this.dirtyMetadata.set(annotationId, {
      taskKey: this.taskKey,
      videoId: VIDEO_ID,
      ...metadata
    });
  }
  onMetadataDelete(annotationId: string): void {
    this.dirtyMetadata.set(annotationId, 'DELETE');
  }
  onFetchedRangesWrite(record: FetchedRangesRecord): void {
    this.pendingFetchedRanges = record;
  }
  getDirtyRecordsInRange(start: number, end: number): {
    upserts: AnnotationFrameRecord[];
    deletedKeys: FrameAnnotationKey[];
  } {
    const upserts: AnnotationFrameRecord[] = [];
    const deletedKeys: FrameAnnotationKey[] = [];
    for (const [key, record] of this.dirtyFrames) {
      if (record === 'DELETE') {
        // 'DELETE' carries no frameIndex/annotationId of its own — the key is the only
        // place they're recorded (see makeFrameKey).
        const [,, frameIndex, annotationId] = (JSON.parse(key) as [string, string, number, string]);
        if (frameIndex >= start && frameIndex <= end) {
          deletedKeys.push({
            frameIndex,
            annotationId
          });
        }
      } else if (record.frameIndex >= start && record.frameIndex <= end) {
        upserts.push(record);
      }
    }
    return {
      upserts,
      deletedKeys
    };
  }
  petitionNow(): void {
    // Only petition if there is actually something to write.
    // Example: evictOutOfWindow calls this before eviction even when the user hasn't
    // made any edits yet — without this guard an empty IDB transaction would open.
    if (this.dirtyFrames.size > 0 || this.dirtyMetadata.size > 0 || this.pendingFetchedRanges !== null) {
      this.petition();
    }
  }
  getStores(): readonly string[] {
    return [Stores.AnnotationFrames, Stores.AnnotationMetadata, Stores.FetchedRanges];
  }
  writeToIdb(tx: IDBTransaction, rollbackChannel: (cb: () => void) => void): void {
    if (this.flushDebounceTimer !== null) {
      clearTimeout(this.flushDebounceTimer);
      this.flushDebounceTimer = null;
    }
    const frameSnapshot = new Map(this.dirtyFrames);
    const metaSnapshot = new Map(this.dirtyMetadata);
    const rangesSnapshot = this.pendingFetchedRanges;
    if (frameSnapshot.size === 0 && metaSnapshot.size === 0 && rangesSnapshot === null) {
      return;
    }
    this.dirtyFrames.clear();
    this.dirtyMetadata.clear();
    this.pendingFetchedRanges = null;
    rollbackChannel(() => {
      for (const [k, v] of frameSnapshot) {
        if (!this.dirtyFrames.has(k)) {
          this.dirtyFrames.set(k, v);
        }
      }
      for (const [k, v] of metaSnapshot) {
        if (!this.dirtyMetadata.has(k)) {
          this.dirtyMetadata.set(k, v);
        }
      }
      if (this.pendingFetchedRanges === null) {
        this.pendingFetchedRanges = rangesSnapshot;
      }
    });
    const frameStore = tx.objectStore(Stores.AnnotationFrames);
    for (const [key, record] of frameSnapshot) {
      if (record === 'DELETE') {
        frameStore.delete((JSON.parse(key) as IDBValidKey));
      } else {
        frameStore.put(record);
      }
    }
    const metaStore = tx.objectStore(Stores.AnnotationMetadata);
    for (const [annotationId, record] of metaSnapshot) {
      if (record === 'DELETE') {
        metaStore.delete([this.taskKey, VIDEO_ID, annotationId]);
      } else {
        metaStore.put(record);
      }
    }
    if (rangesSnapshot !== null) {
      tx.objectStore(Stores.FetchedRanges).put(rangesSnapshot);
    }
  }
  private scheduleDebouncedFlushCheck(): void {
    if (this.flushDebounceTimer !== null) {
      clearTimeout(this.flushDebounceTimer);
    }
    this.flushDebounceTimer = setTimeout(() => {
      this.flushDebounceTimer = null;
      if (this.shouldFlush()) {
        this.petition();
      }
    }, DIRTY_FLUSH_DEBOUNCE_MS);
  }
  private shouldFlush(): boolean {
    return this.dirtyFrames.size + this.dirtyMetadata.size >= DIRTY_FLUSH_THRESHOLD;
  }
  private makeFrameKey(frameIndex: number, annotationId: string): string {
    return JSON.stringify([this.taskKey, VIDEO_ID, frameIndex, annotationId]);
  }
  private makeFrameRecord(frameIndex: number, annotationId: string, payload: AnnotationWritePayload): AnnotationFrameRecord {
    return {
      taskKey: this.taskKey,
      videoId: VIDEO_ID,
      frameIndex,
      annotationId,
      geometry: payload.geometry,
      isKeyFrame: payload.isKeyFrame,
      keyFrameType: payload.keyFrameType
    };
  }
}